// Types for the Federal Reserve SCF cohort dataset bundled at
// src/data/scf-percentiles.json. Generated by scripts/data/sources/scf.ts.

export type CohortDimension =
  | 'networth'
  | 'financial_assets'
  | 'nonfin_assets'
  | 'retirement'
  | 'home_equity'
  | 'liquid';

export const AGE_BANDS = ['<35', '35-44', '45-54', '55-64', '65-74', '75+'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

export const INCOME_BANDS = [
  '<25k',
  '25-50k',
  '50-75k',
  '75-100k',
  '100-150k',
  '150-200k',
  '200k+',
] as const;
export type IncomeBand = (typeof INCOME_BANDS)[number];

export type CohortSplit =
  | { kind: 'all' }
  | { kind: 'age'; band: AgeBand }
  | { kind: 'income'; band: IncomeBand };

export type PercentileKey = 'p10' | 'p25' | 'p50' | 'p75' | 'p90' | 'p95' | 'p99';
export const PERCENTILE_KEYS: PercentileKey[] = ['p10', 'p25', 'p50', 'p75', 'p90', 'p95', 'p99'];

export type PercentileRow = Record<PercentileKey, number>;

export interface DatasetMeta {
  sourceId: string;
  sourceUrl: string;
  sourceVersion: string;
  buildAt: string;
  sha256OfRaw?: string;
  notes?: string;
}

export interface ScfDataset {
  meta: DatasetMeta;
  dataYear: number;
  dollarYear: number;
  cpiAdjustment: number;
  ageBands: readonly AgeBand[];
  incomeBands: readonly IncomeBand[];
  dimensions: readonly CohortDimension[];
  percentiles: readonly number[];
  cohorts: Record<string, Record<CohortDimension, PercentileRow>>;
}

/** Stable cohort key matching what the build script emits. */
export function cohortKey(split: CohortSplit): string {
  if (split.kind === 'all') return 'all';
  return `${split.kind}:${split.band}`;
}

/** Human-readable label for a cohort split, used in UI titles. */
export function cohortLabel(split: CohortSplit): string {
  if (split.kind === 'all') return 'all US households';
  if (split.kind === 'age') return `ages ${split.band}`;
  return `income ${split.band}`;
}
